// @spec AUD-STR-035
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Shield } from 'lucide-react';
import { useState } from 'react';
import { Toaster } from 'sonner';

import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AuditorAuditEventDetail } from '@/features/auditor-access/components/AuditorAuditEventDetail';
import { AuditorAuditLogList } from '@/features/auditor-access/components/AuditorAuditLogList';
import { AuditorModeBanner } from '@/features/auditor-access/components/AuditorModeBanner';
import { useAuditorStore } from '@/stores/auditor-store';

export const Route = createFileRoute('/auditor/access/$accessId/audit')({
  component: AuditorAuditPage,
});

/**
 * Auditor audit logs page.
 *
 * Displays a read-only list of audit events for an organization
 * that the auditor has access to review.
 */
function AuditorAuditPage() {
  const { accessId } = Route.useParams();
  const navigate = useNavigate();

  const { t } = useTranslation('auditor-access');

  const isAuditorMode = useAuditorStore.use.isAuditorMode();
  const currentAccess = useAuditorStore.use.currentAccess();

  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  // Verify user is in auditor mode and has the correct access ID
  if (!isAuditorMode || !currentAccess) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.accessRequired')}</AlertTitle>
            <AlertDescription>
              {t('routes.accessRequiredDescription')}
            </AlertDescription>
          </Alert>
          <Button variant="outline" className="mt-4" onClick={() => void navigate({ to: '/' })}>
            {t('routes.goToHome')}
          </Button>
        </div>
      </div>
    );
  }

  // Verify the access ID matches the current auditor mode
  if (currentAccess.accessId !== accessId) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.invalidAccess')}</AlertTitle>
            <AlertDescription>
              {t('routes.invalidAccessDescription')}
            </AlertDescription>
          </Alert>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => void navigate({ to: `/auditor/access/${currentAccess.accessId}/audit` })}
          >
            {t('routes.goToYourAuditLog')}
          </Button>
        </div>
      </div>
    );
  }

  // Verify the access scope includes 'audit_logs'
  if (!currentAccess.accessScope.includes('audit_logs')) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.accessDenied')}</AlertTitle>
            <AlertDescription>
              {t('routes.accessDeniedAuditLog')}
            </AlertDescription>
          </Alert>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => void navigate({ to: '/dashboard' })}
          >
            {t('routes.goToDashboard')}
          </Button>
        </div>
      </div>
    );
  }

  const handleEventClick = (eventId: string) => {
    setSelectedEventId(eventId);
  };

  const handleBackToList = () => {
    setSelectedEventId(null);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Auditor mode banner */}
      <AuditorModeBanner hasOtherOrganizations={false} />

      <div className="p-8">
        <div className="mx-auto max-w-6xl space-y-6">
          {/* Page header - only shown in list view */}
          {!selectedEventId && (
            <header className="space-y-1">
              <div className="flex items-center gap-2">
                <Shield className="h-6 w-6 text-amber-600" aria-hidden="true" />
                <h1 className="text-2xl font-semibold text-foreground">{t('routes.auditorAuditLogReview')}</h1>
              </div>
              <p className="text-muted-foreground">
                {t('routes.readOnlyAccessToAuditLog', { orgName: currentAccess.name })}
              </p>
            </header>
          )}

          {/* Content */}
          {selectedEventId ? (
            <AuditorAuditEventDetail
              accessId={accessId}
              eventId={selectedEventId}
              onBack={handleBackToList}
            />
          ) : (
            <AuditorAuditLogList
              accessId={accessId}
              organizationName={currentAccess.name}
              onEventClick={handleEventClick}
            />
          )}
        </div>
      </div>

      {/* Toast notifications */}
      <Toaster position="top-right" richColors />
    </div>
  );
}
